import { computed, Signal, signal } from '@angular/core';

/**
 * Reactive, signal-based pagination controller shared across tables.
 * It derives a paged view from any source signal without mutating it,
 * so the same logic powers every table in the app without duplication.
 */
export interface PaginationController<T> {
    /** Current page, clamped to the valid range. */
    readonly page: Signal<number>;
    readonly pageSize: Signal<number>;
    readonly pageSizeOptions: readonly number[];
    /** Total items in the (already filtered) source. */
    readonly totalItems: Signal<number>;
    readonly totalPages: Signal<number>;
    /** Items belonging to the current page. */
    readonly paged: Signal<T[]>;
    /** 1-based index of the first item on the page (0 when empty). */
    readonly pageStart: Signal<number>;
    /** 1-based index of the last item on the page. */
    readonly pageEnd: Signal<number>;
    /** Windowed page numbers to render as buttons. */
    readonly pageNumbers: Signal<number[]>;

    goToPage(page: number): void;
    next(): void;
    prev(): void;
    setPageSize(size: number): void;
    /** Resets back to the first page (e.g. after filters change). */
    reset(): void;
}

export interface PaginationOptions {
    pageSize?: number;
    pageSizeOptions?: readonly number[];
    /** Max number of numbered page buttons to display. */
    maxButtons?: number;
}

export interface ServerPaginationOptions extends PaginationOptions {
    onParamsChange: (params: { offset: number; limit: number }) => void;
}

/**
 * Pagination controller backed by server-side offset/limit queries.
 * `items` holds the current page; `totalItems` reflects the API meta count.
 */
export function createServerPagination<T>(
    items: Signal<readonly T[]>,
    totalItems: Signal<number>,
    options: ServerPaginationOptions,
): PaginationController<T> {
    const pageSizeOptions = options.pageSizeOptions ?? [25, 50, 100, 200];
    const maxButtons = options.maxButtons ?? 5;
    const pageSize = signal<number>(options.pageSize ?? pageSizeOptions[1] ?? 50);
    const currentPage = signal<number>(1);

    const totalPages = computed<number>(() =>
        Math.max(1, Math.ceil(totalItems() / pageSize()))
    );

    const page = computed<number>(() =>
        Math.min(Math.max(1, currentPage()), totalPages())
    );

    const paged = computed<T[]>(() => items() as T[]);

    const pageStart = computed<number>(() =>
        totalItems() === 0 ? 0 : (page() - 1) * pageSize() + 1
    );

    const pageEnd = computed<number>(() =>
        Math.min(page() * pageSize(), totalItems())
    );

    const pageNumbers = computed<number[]>(() => {
        const total = totalPages();
        const current = page();

        if (total <= maxButtons) {
            return Array.from({ length: total }, (_, i) => i + 1);
        }

        const half = Math.floor(maxButtons / 2);
        let start = Math.max(1, current - half);
        const end = Math.min(total, start + maxButtons - 1);
        start = Math.max(1, end - maxButtons + 1);

        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    });

    const toQuery = (pageNumber: number): { offset: number; limit: number } => ({
        offset: Math.max(0, (Math.max(1, pageNumber) - 1) * pageSize()),
        limit: pageSize(),
    });

    const notifyChange = (pageNumber: number): void => {
        options.onParamsChange(toQuery(pageNumber));
    };

    const goToPage = (target: number): void => {
        const nextPage = Math.min(Math.max(1, target), totalPages());
        if (nextPage === currentPage()) {
            return;
        }

        currentPage.set(nextPage);
        notifyChange(nextPage);
    };

    return {
        page,
        pageSize: pageSize.asReadonly(),
        pageSizeOptions,
        totalItems,
        totalPages,
        paged,
        pageStart,
        pageEnd,
        pageNumbers,
        goToPage,
        next: () => goToPage(currentPage() + 1),
        prev: () => goToPage(currentPage() - 1),
        setPageSize: (size: number) => {
            if (size === pageSize()) {
                return;
            }

            pageSize.set(size);
            currentPage.set(1);
            notifyChange(1);
        },
        reset: () => {
            currentPage.set(1);
            notifyChange(1);
        },
    };
}

export function createPagination<T>(
    source: Signal<readonly T[]>,
    options: PaginationOptions = {}
): PaginationController<T> {
    const pageSizeOptions = options.pageSizeOptions ?? [5, 10, 25, 50, 100];
    const maxButtons = options.maxButtons ?? 5;

    const pageSize = signal<number>(options.pageSize ?? pageSizeOptions[0]);
    const currentPage = signal<number>(1);

    const totalItems = computed<number>(() => source().length);

    const totalPages = computed<number>(() =>
        Math.max(1, Math.ceil(totalItems() / pageSize()))
    );

    const page = computed<number>(() =>
        Math.min(Math.max(1, currentPage()), totalPages())
    );

    const paged = computed<T[]>(() => {
        const size = pageSize();
        const start = (page() - 1) * size;
        return source().slice(start, start + size) as T[];
    });

    const pageStart = computed<number>(() =>
        totalItems() === 0 ? 0 : (page() - 1) * pageSize() + 1
    );

    const pageEnd = computed<number>(() =>
        Math.min(page() * pageSize(), totalItems())
    );

    const pageNumbers = computed<number[]>(() => {
        const total = totalPages();
        const current = page();

        if (total <= maxButtons) {
            return Array.from({ length: total }, (_, i) => i + 1);
        }

        const half = Math.floor(maxButtons / 2);
        let start = Math.max(1, current - half);
        const end = Math.min(total, start + maxButtons - 1);
        start = Math.max(1, end - maxButtons + 1);

        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    });

    const goToPage = (target: number): void => {
        currentPage.set(Math.min(Math.max(1, target), totalPages()));
    };

    return {
        page,
        pageSize: pageSize.asReadonly(),
        pageSizeOptions,
        totalItems,
        totalPages,
        paged,
        pageStart,
        pageEnd,
        pageNumbers,
        goToPage,
        next: () => goToPage(page() + 1),
        prev: () => goToPage(page() - 1),
        setPageSize: (size: number) => {
            pageSize.set(size);
            currentPage.set(1);
        },
        reset: () => currentPage.set(1),
    };
}
